<script setup>
import {required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import NewCustomer from "@/components/NewCustomerModal.vue";
import CustomerProfile from "@/components/CustomerProfile/index.vue";
import {AutoComplete, FloatLabel} from "primevue";
import {computed, provide, reactive, ref, watch, nextTick} from "vue";
import {useCustomerStore} from "@/stores/customer.js";
import {useUserStore} from "@/stores/user.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {showMessage} from "@/utils/showMessage.js";

const userStore = useUserStore();
const customerStore = useCustomerStore();

// Props
const props = defineProps({
  modelValue: Object,
  appendTo: {default: "body"},
  label: {default: "User"},
  containerClass: {type: String},
  accountName: {type: String},
  accountID: {type: [String, Number]},
  required: {type: Boolean, default: true},
  disabled: {type: Boolean, default: false},
  readonly: {type: Boolean, default: false}
});

// Emits
const emit = defineEmits(["update:modelValue", "select"]);

// component variables
const form = reactive({
  "id": "",
})
const rules = computed(() => {
  return {
    id: props.required ? {required} : {}
  };
});
const $vuelidate = useVuelidate(rules, form);
const loading = ref(false)
const selectedCustomer = ref({})
const selectedUser = ref({})
const searchMode = ref(null)
const keyword = ref('')
const serverUsers = ref([])
const isShowAddNewCustomerModal = ref(false);
const isShowCustomerProfileModal = ref(false);
const canShowCustomerProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))
const autoCompleteRef = ref(null)

// component functions
const optionLabel = (item) => {
  if (!item) return '';
  if (item.name && item.discordUsername) {
    return `${item.name} - ${item.discordUsername}`;
  }
  return item.name
      ? item.name
      : item.storeID ? item.storeID : '';
};

function getDiscordCreationDate(entityId) {
  if (!entityId) return null;
  const TIMESTAMP_OFFSET = 22n;
  const DISCORD_EPOCH = 1420070400000n;
  const id = BigInt(entityId);
  const timestamp = (id >> TIMESTAMP_OFFSET) + DISCORD_EPOCH;
  return gregorianDate(Number(timestamp), 'HH:mm - DD MMM YYYY');
}

async function searchInCustomers(event, focus) {
  const myKeyword = event.query?.trim()
  keyword.value = myKeyword

  // reset کامل
  searchMode.value = "customers"

  if (!myKeyword || myKeyword.length < 3) {
    serverUsers.value = []
    loading.value = false
    return
  }

  loading.value = true
  try {
    const result = await customerStore.action_searchCustomers({"keyword": myKeyword})
    serverUsers.value = result || []
  } finally {
    loading.value = false
  }
}

async function searchInUsers() {
  if (!keyword.value || keyword.value.length < 3) {
    serverUsers.value = []
    loading.value = false
    return
  }

  loading.value = true
  try {
    const result = await userStore.action_searchAccounts({"keyword": keyword.value})
    serverUsers.value = result || []
  } finally {
    loading.value = false
  }
}

const handleCompleteSearch = async () => {
  searchMode.value = "all"
  await searchInUsers()
}

const handleAddNewCustomer = () => {
  isShowAddNewCustomerModal.value = true
}

const handleOptionClick = async (option, event) => {
  if (searchMode.value !== 'all') return

  event.stopPropagation()
  event.preventDefault()

  try {
    loading.value = true
    const res = await customerStore.action_addNewCustomer({
      id: option.id,
      store: 'Direct',
      storeID: option.name,
      name: option.name
    })

    if (res.result === true) {
      showMessage('New Customer added!', 'success')
      const customerID = res.data.id

      selectedCustomer.value = {
        id: customerID,
        store: 'Direct',
        storeID: option.name,
        name: option.name,
        discordUsername: option.discordUsername,
        discordDisplayName: option.discordDisplayName
      }
      await nextTick()
      onSelect()
      autoCompleteRef.value?.hide()
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    loading.value = false
  }
}

function showCustomerProfileModal(user) {
  if (user.isLoading) return;
  if (canShowCustomerProfile.value) {
    customerStore.setSelectedCustomer(user);
    isShowCustomerProfileModal.value = true
  }
}

async function onSelect() {
  if (!selectedCustomer.value?.id) {
    form.id = null
    emit('update:modelValue', null)
    emit('select', null)
    return
  }
  form.id = selectedCustomer.value.id
  emit('select', selectedCustomer.value)
  emit('update:modelValue', selectedCustomer.value)
}

const handleSelectNewCustomer = async (customer) => {
  selectedCustomer.value = customer
  await nextTick()
  await onSelect()
}

watch(selectedCustomer, (newVal) => {
  if (!newVal || Object.keys(newVal).length === 0 || !newVal.id) {
    form.id = null
    emit('update:modelValue', null)
    emit('select', null)
  }
}, {deep: true})

watch(() => props.modelValue, (newVal) => {
  if (newVal && typeof newVal === 'object') {
    selectedCustomer.value = newVal
    form.id = newVal.id
  }
}, {immediate: true})

watch(keyword, () => {
  serverUsers.value = []
  searchMode.value = "customers"
})

provide('isShowCustomerProfileModal', isShowCustomerProfileModal)
provide('isShowAddNewCustomerModal', isShowAddNewCustomerModal)
</script>

<template>
  <div class="flex-1 flex flex-col gap-0.5" :class="containerClass">
    <FloatLabel class="w-full custom-select" :class="{ 'has-error': $vuelidate.id.$error}" variant="on">
      <AutoComplete v-model="selectedCustomer" input-id="customer" :spellcheck="false" ref="autoCompleteRef"
                    :suggestions="serverUsers" :forceSelection="!!props.required" :showClear="true"
                    @complete="searchInCustomers($event, false)" completeOnFocus
                    :optionLabel="optionLabel" @itemSelect="onSelect" :minLength="3" :delay="1000"
                    @focus="searchInCustomers($event, true)"
                    :loading="loading" class="w-full !rounded-md"
                    :class="[$vuelidate.id.$error ? '!border !border-red-300' : '']"
                    input-class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                    overlay-class="!bg-[#0F172A] !rounded-md !z-[900]" :append-to="appendTo"
                    :virtualScrollerOptions="{ itemSize: 48 }"
                    dropdown :disabled="disabled" :readonly="readonly"
                    :pt="{option: {class: '!p-0'}}"
      >
        <template #header>
          <div v-if="keyword" class="p-2 border-b border-slate-700 flex justify-between items-center px-3 py-2">
            <!-- Add New -->
            <button
                v-if="searchMode === 'all'"
                class="text-green-400 text-sm"
                @click.stop="handleAddNewCustomer"
            >
              ➕ Add New Customer
            </button>

            <!-- Search All -->
            <button
                v-else-if="searchMode === 'customers' || serverUsers.length"
                class="text-blue-400 text-sm"
                @click.stop="handleCompleteSearch"
            >
              🔍 Complete Search
            </button>
          </div>
        </template>
        <template #option="slotProps">
          <div @click="handleOptionClick(slotProps.option, $event)" class="flex items-center gap-2 text-sm w-full">
            <div @click.stop="showCustomerProfileModal(slotProps.option)"
                 :class="[canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
              <div class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-user-icon lucide-user">
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
            </div>
            <div class="flex-1 flex flex-col">
              <div class="flex flex-row gap-2 items-center">
                <div>{{ slotProps.option.name }} &nbsp &nbsp</div>
                <div v-if="slotProps.option.rating" class="flex flex-row items-center gap-0.5 text-xs">
                  <span class="flex flex-row items-end">
                    {{ slotProps.option.rating }}⭐ ({{ slotProps.option.ratingCount }})
                  </span>
                </div>
              </div>
              <div class="flex flex-row w-max text-xs text-slate-400">
                <div>{{ slotProps.option.discordUsername ?? "ــــــ" }}</div>
                <div>{{
                    "&nbsp- " + (slotProps.option.discordDisplayName ? slotProps.option.discordDisplayName : "ــــــ")
                  }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </AutoComplete>
      <label for="customer" class="!bg-slate-900">{{ label }}</label>
    </FloatLabel>
    <template v-if="$vuelidate.id.$error">
      <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
         v-if="!$vuelidate.id.required.$response">
        This field is required
      </p>
    </template>
  </div>
  <CustomerProfile v-if="isShowCustomerProfileModal" :userID="customerStore.selectedCustomer.id"/>
  <NewCustomer v-if="isShowAddNewCustomerModal" :selectedUser="selectedUser" @selectCustomer="handleSelectNewCustomer"/>
</template>

<style scoped lang="scss">

</style>